import { toast } from 'sonner';
import { create } from 'zustand';

type AlertType = 'success' | 'error' | 'info';

const DEFAULT_ERROR_MESSAGE = 'Something went wrong. Please try again.';

interface IErrorLike {
	message?: unknown;
}

interface IAlertStore {
	show: (message: string, type?: AlertType) => void;
	showSuccess: (message: string) => void;
	showError: (error: IErrorLike | null | undefined, fallback?: string) => void;
}

function showToast(message: string, type: AlertType) {
	// Request helpers don't always honour their own typed contract (some fall back to the whole response body as
	// `message` when the server omits one), so never trust it's a string even though the type says it is.
	const safeMessage =
		typeof message === 'string' ? message : DEFAULT_ERROR_MESSAGE;
	if (type === 'error') {
		toast.error(safeMessage);
	} else if (type === 'info') {
		toast.info(safeMessage);
	} else {
		toast.success(safeMessage);
	}
}

/** The one way every Workinance app tells the user something happened. Needs <AlertComponent /> mounted once. */
export const useAlertStore = create<IAlertStore>((_set, get) => ({
	show: (message, type = 'success') => showToast(message, type),
	showSuccess: (message) => get().show(message, 'success'),
	showError: (error, fallback = DEFAULT_ERROR_MESSAGE) => {
		get().show(
			typeof error?.message === 'string' ? error.message : fallback,
			'error',
		);
	},
}));
